/*
 * Authors: mariushirjanu
 * Created: 10/11/2023
 */
import * as _ from 'underscore';
import { util } from '../util';
import { Component, Input, ViewEncapsulation, SimpleChanges, OnChanges, OnDestroy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaStateService, TranslationCategory } from '../services/ga-state.service';
import { IExtraFieldConfigurationObject } from '../formly/ga-formly.service';
import { IObject } from '../../../type_definitions/datatypes';
import { ReportFiltersUtil } from '../report/report-filters-util.service';
import { ReportWrapperService } from '../report/report-wrapper.service';
import { ScrollService } from '../scroll/scroll.service';
import { MaxWidth, ResponsiveService } from '../responsive/responsive.service';
import { STATE_BROWSELIST, STATE_REPORT } from '../app-states';
import { GridOptions } from 'ag-grid-community';
import { ITrackVisibilityOptions } from '../track-visibility/track-visibility.directive';
import { ReportParameterCode, ReportParameterShortCode } from '@app/main/report/report-state.util';

export interface ICollapsibleSection {
  key:string;
  id?:string;
  title?:string;
  fields?:ICollapsibleSectionFields;
  isCollapsed?:boolean;
  showSection?:boolean;
  isFullWidth?:boolean;  // when set to true, the section takes the maximum screen width (useful for reports)
  isFullHeight?:boolean;  // when set to true, the section takes the maximum screen height (useful for reports)
  report?:ICollapsibleSectionReport;
  category?:TranslationCategory;
  clickable?:boolean;   // when set to false, the section cannot be clicked to collapse/expand
  expanded?:{
    iframe?:{
      src:string;
      height?:string;
      scrolling?:boolean;
    };
    report?: ICollapsibleSectionReport;
  };
  isVisible?:boolean;  // only to be used internally/do not assign in the configuration
}

export interface ICollapsibleSectionFields {
  collapsed?:IExtraFieldConfigurationObject[];
  expanded?:IExtraFieldConfigurationObject[];
}

export interface ICollapsibleSectionReport {
  id:number;
  filters?:{ [ key:string ]:any };
  refState?:string;
  refParams?:{ [ key:string ]:any };
  storedReport?:string;
  browseListCode?:string;
  gridHeight?:string;
  height?:number;
  gridOptions?:GridOptions;
}

@Component({
  selector: 'collapsible-sections',
  template: `
    <div class="collapsible-sections">
      <h2>
        <text key="HEADING"></text>
      </h2>
      <div *ngIf="!loading">
        <div class="col-space-bottom" *ngFor="let section of formSections"> <!-- repeat the sections -->
          <div *ngIf="section.showSection" [id]="section.id" class="section" [ngClass]="{ 'section-minimum': !section.isFullWidth }" [trackVisibility]="trackVisibilityOptions" (isVisible)="onSectionVisibility(section, $event)">
            <editable-text [key]="section.key" [category]="section.category" menu="title" highlight="h3 a:eq(0)"></editable-text>
            <collapsible-pane class="pane-detail" title="{{ screen + '.' + section.key + '.TITLE' | translate }}" [collapsed]="section.isCollapsed" [clickable]="section.clickable" (onChange)="onSectionCollapsed(section, $event)">
              <div class="collapsible-title-extra" *ngIf="section?.expanded?.report?.id"> <!-- content projection: collapsible-title-extra -->
                <span class="detailed-report-link"><a [uiSref]="section.expanded.report.refState" [uiParams]="section.expanded.report.refParams" target="_blank"><i class="fa fa-table"></i></a></span>
              </div>
              <div class="form-horizontal collapse-section"> <!-- expanded fields -->
                <formly *ngIf="section.fields?.expanded" [form]="form" [fields]="section.fields.expanded" [model]="model"></formly>
                <report-wrapper *ngIf="section.expanded?.report?.id && !section?.expanded?.iframe?.src" [reportId]="section.expanded.report.id" [filters]="section.expanded.report.filters" [autoload]="!section.isCollapsed" [storedReport]="section.expanded.report.storedReport" [height]="section.expanded.report.height" [gridOptions]="section.expanded.report.gridOptions"></report-wrapper>
              </div>
            </collapsible-pane>
            <collapsible-pane class="pane-summary" [collapsed]="!section.isCollapsed" [clickable]="section.clickable">
              <div *ngIf="section.isCollapsed" class="form-horizontal collapse-section"> <!-- collapsed fields -->
                <formly *ngIf="section.fields?.collapsed" [form]="form" [fields]="section.fields.collapsed" [model]="model"></formly>
              </div>
            </collapsible-pane>
          </div>
        </div>
      </div>
      <h3 *ngIf="loading">
        <activity-indicator useDefaultText="true"></activity-indicator>
      </h3>
    </div>
  `,
  styleUrls: [ './collapsible-sections.scss' ],
  encapsulation: ViewEncapsulation.None
})
export class CollapsibleSectionsComponent implements OnChanges, OnDestroy {
  @Input() public formSections:ICollapsibleSection[];
  @Input() public model:any;
  @Input() public loading:boolean;

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public screen:string;
  public trackVisibilityOptions:ITrackVisibilityOptions = { topMarginExclude: '150px;.ui-layout-north;.ui-layout-resizer-north;.navbar-wizard' };

  constructor(
    private responsiveService:ResponsiveService,
    private scrollService:ScrollService,
    private reportWrapperService:ReportWrapperService,
    private reportFiltersUtil:ReportFiltersUtil,
    stateService:GaStateService
  ) {
    this.responsiveService.width = MaxWidth.LG;
    this.screen = stateService.screen?.toUpperCase();
  }

  public ngOnChanges(changesObj:SimpleChanges):void {
    if (changesObj.formSections) {
      this.formSections = this.processFormSections(changesObj.formSections.currentValue);
    }

    if (changesObj.model) {
      this.model = changesObj.model.currentValue;
    }

    if (changesObj.loading) {
      this.loading = changesObj.loading.currentValue;
    }
  }

  private processFormSections(formSections:ICollapsibleSection[]):ICollapsibleSection[] {
    _.each(formSections, (section:ICollapsibleSection, i:number) => {
      // add sections ids based on section's key
      formSections[ i ].id = formSections[ i ].id || this.getSectionId(section.key);

      formSections[ i ].title = `WEBSHOP.B2B_SETTINGS.${section.key}.TITLE`;

      if (section?.expanded?.report?.id) {
        formSections[ i ].expanded.report.refParams = this.getReportSrefParams(section.expanded.report);

        // set full report height
        if(formSections[ i ].isFullHeight && !formSections[ i ].report.height){
          formSections[ i ].expanded.report.gridHeight = '100px;.ui-layout-north;.ui-layout-resizer-north';
        }

        formSections[ i ].expanded.report.refState = formSections[ i ].expanded.report.browseListCode ? STATE_BROWSELIST : STATE_REPORT;
      }

      // non-configured section are shown by default
      if (util.isNullOrUndefined(formSections[ i ].showSection)) {
        formSections[ i ].showSection = true;
      }

      // non-configured section are collapsed by default
      if (util.isNullOrUndefined(formSections[ i ].isCollapsed)) {
        formSections[ i ].isCollapsed = true;
      }
    });

    return formSections;
  }

  public getReportSrefParams(report:ICollapsibleSectionReport):IObject {
    const refParams:IObject = {
      [ReportParameterCode.REPORT_ID]: report.id,
      [ReportParameterShortCode.FILTERS]: this.reportFiltersUtil.filtersToParam(report.filters),
      [ReportParameterShortCode.SHOW_FILTERS]: true,
      [ReportParameterShortCode.AUTO_LOAD]: true
    };

    if (report.storedReport) {
      refParams.storedReport = report.storedReport;
    }

    if(report.browseListCode){
      refParams.listCode = report.browseListCode;
    }

    return refParams;
  }

  public onSectionCollapsed(section:ICollapsibleSection, collapsed:boolean):void {
    section.isCollapsed = collapsed;
    if (!collapsed) {

      // load the report when after the section has been expanded
      if (section?.expanded?.report?.id) {
        this.reportWrapperService.loadReportData(section.expanded.report.id);
      }

      if(!section.isVisible) {
        setTimeout(() => {
          this.scrollService.scrollToElement(`#${this.getSectionId(section.key)}`);
        }, 500);
      }
    }
  }

  // updates whenever the section is visible inside the viewport
  public onSectionVisibility(section:ICollapsibleSection, visible:boolean){
    section.isVisible = visible;
  }

  public getSectionId(key:string):string {
    return key && `section-${key.replace(/_/g, '-').toLowerCase()}`;
  }

  public ngOnDestroy():void {
    this.responsiveService.width = MaxWidth.SM; // revert width
  }
}